
import React, { useState } from 'react';
import { CategoryTree } from '@/components/CategoryTree';
import { CategoryDialogManager } from './CategoryDialogManager';
import { MappingRulesSection } from './mapping-rules/MappingRulesSection';
import { CategoryActionsSection } from './category/CategoryActionsSection';
import { Category, Account, MappingRule } from '@/types';
import { useCategorySelection } from '@/hooks/useCategorySelection';
import { toast } from 'sonner';

interface CategorySectionProps {
  categories: Category[];
  accounts: Account[];
  onDrop: (accountId: string, categoryId: string) => void;
  expanded: Set<string>;
  onToggle: (categoryId: string) => void;
  onAddCategory: (name: string, parentId: string | null, type: 'Afdracht' | 'Geen afdracht') => void;
  onEditCategory: (categoryId: string, newName: string, newParentId: string | null, newType: 'Afdracht' | 'Geen afdracht') => void;
  onReorderCategories?: (categories: Category[]) => void;
  onRemoveAccount?: (accountId: string) => void;
  onSetAccounts: (accounts: Account[]) => void;
  setCategories: (categories: Category[]) => void;
}

export const CategorySection: React.FC<CategorySectionProps> = ({
  categories,
  accounts,
  onDrop,
  expanded,
  onToggle,
  onAddCategory,
  onEditCategory,
  onReorderCategories,
  onRemoveAccount,
  onSetAccounts,
  setCategories,
}) => {
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  const { selectedCategory, setSelectedCategory, handleCategorySelect } = useCategorySelection(categories, onToggle, expanded);

  const handleUpdateMappingRules = (categoryId: string, updatedRules: MappingRule[]) => {
    const updateCategoryRules = (cats: Category[]): Category[] => {
      return cats.map((cat) => {
        if (cat.id === categoryId) {
          return { ...cat, mappingRules: updatedRules };
        }
        if (cat.children.length > 0) {
          return {
            ...cat,
            children: updateCategoryRules(cat.children),
          };
        }
        return cat;
      });
    };

    const updatedCategories = updateCategoryRules(categories);
    setCategories(updatedCategories);
  };

  const handleDeleteCategory = (categoryId: string) => {
    if (selectedCategory?.id === categoryId) {
      setSelectedCategory(null);
    }
    
    const removeCategory = (cats: Category[]): Category[] => {
      return cats.filter(cat => {
        if (cat.id === categoryId) {
          return false;
        }
        if (cat.children.length > 0) {
          cat.children = removeCategory(cat.children);
        }
        return true;
      });
    };

    const updatedCategories = removeCategory([...categories]);
    setCategories(updatedCategories);
  };

  const handleExpandAll = () => {
    const allCategoryIds = new Set<string>();
    
    const collectIds = (cats: Category[]) => {
      cats.forEach(cat => {
        allCategoryIds.add(cat.id);
        if (cat.children.length > 0) {
          collectIds(cat.children);
        }
      });
    };
    
    collectIds(categories);
    allCategoryIds.forEach(id => {
      if (!expanded.has(id)) {
        onToggle(id);
      }
    });
    toast.success('All categories expanded');
  };

  const handleCollapseAll = () => {
    expanded.forEach(id => {
      onToggle(id);
    });
    toast.success('All categories collapsed');
  };

  return (
    <div className="space-y-4 -mt-[22px]">      
      <div className="rounded-lg p-4 mt-2">
        <CategoryDialogManager
          categories={categories}
          onAddCategory={onAddCategory}
          onEditCategory={onEditCategory}
          open={addDialogOpen}
          onOpenChange={setAddDialogOpen}
          selectedCategory={selectedCategory}
          editDialogOpen={editDialogOpen}
          onEditDialogOpenChange={setEditDialogOpen}
        />
        
        <div className="container mx-auto max-w-[150%]">
          <div className="grid grid-cols-2 gap-6 mt-4">
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Categories</h2>
              <div className="rounded-lg border p-4">
                <CategoryActionsSection
                  onAddCategory={() => setAddDialogOpen(true)}
                  onExpandAll={handleExpandAll}
                  onCollapseAll={handleCollapseAll}
                />
                <div className="h-[calc(100vh-16rem)] overflow-y-auto">
                  <CategoryTree
                    categories={categories}
                    accounts={accounts}
                    onDrop={onDrop}
                    onToggle={onToggle}
                    expanded={expanded}
                    onEdit={(category) => {
                      setSelectedCategory(category);
                      setEditDialogOpen(true);
                    }}
                    onReorder={onReorderCategories}
                    onRemoveAccount={onRemoveAccount}
                    onDeleteCategory={handleDeleteCategory}
                    onSelect={handleCategorySelect}
                    selectedCategory={selectedCategory}
                  />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h2 className="text-lg font-semibold">Mapping Rules</h2>
              <div className="rounded-lg border">
                <MappingRulesSection
                  selectedCategory={selectedCategory}
                  onUpdateCategory={handleUpdateMappingRules}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
